import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { UtensilsCrossed } from "lucide-react";

import {
  useCreateOrUpdateMenuItemMutation,
  useGetProductsQuery,
} from "@/redux/services/menu/menu.api";
import { Product, Section } from "@/redux/services/menu/menu.type";
import { useAppSelector } from "@/redux/features/hooks";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { SearchInput } from "@/components/SearchInput";
import { displayPrice } from "@/lib/utils";
import { useRestaurantCurrency } from "@/lib/hooks";
import {
  placeDishesSequentially,
  summarizeDishPlacement,
} from "@/lib/placeExistingDishes";
import { hasMorePages, mergePage } from "@/lib/pagedList";
import { useDebounce } from "@/hooks/useDebounce";

// A restaurant can have hundreds of dishes: search on the server and load
// them page by page instead of loading the whole catalogue.
const PAGE_SIZE = 50;

interface AddExistingDishModalProps {
  isOpen: boolean;
  onClose: () => void;
  section?: Section;
  onSuccess?: () => void;
}

/**
 * Places a dish the restaurant already has onto this menu.
 *
 * Recreating a dish by hand for a second menu is what used to fragment the
 * reports: the two copies were separate items, so sales, recipes and margins
 * were split between them. Placing the existing dish keeps one identity, and
 * the placement carries only this menu's price and availability.
 */
const AddExistingDishModal = ({
  isOpen,
  onClose,
  section,
  onSuccess,
}: AddExistingDishModalProps) => {
  const { currentRestaurant } = useAppSelector((s) => s.user);
  const currency = useRestaurantCurrency();
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery.trim(), 300);
  // Search term and page move together, so a new search never fetches a
  // later page of the new results.
  const [query, setQuery] = useState({ q: "", page: 1 });
  const { page } = query;
  const [loadedDishes, setLoadedDishes] = useState<Product[]>([]);
  const [hasMore, setHasMore] = useState(false);
  // Kept by id with the dish itself, so a selection survives a new search.
  const [selected, setSelected] = useState<Map<string, Product>>(new Map());
  const [isPlacing, setIsPlacing] = useState(false);

  const { data, isLoading, isFetching } = useGetProductsQuery(
    {
      restaurantId: currentRestaurant?.id as string,
      q: query.q || undefined,
      page,
      limit: PAGE_SIZE,
    },
    { skip: !currentRestaurant?.id || !isOpen }
  );
  const [placeDish] = useCreateOrUpdateMenuItemMutation();

  // Start clean every time the picker opens.
  useEffect(() => {
    if (isOpen) {
      setSelected(new Map());
      setSearchQuery("");
      setQuery({ q: "", page: 1 });
    }
  }, [isOpen]);

  // A new search starts again from the first page.
  useEffect(() => {
    setQuery({ q: debouncedSearch, page: 1 });
  }, [debouncedSearch]);

  useEffect(() => {
    if (!data?.results) return;
    setLoadedDishes((prev) => mergePage(prev, data.results, page));
    setHasMore(
      hasMorePages({
        loaded: (page - 1) * PAGE_SIZE + data.results.length,
        total: data.count,
        lastPageSize: data.results.length,
        pageSize: PAGE_SIZE,
      })
    );
  }, [data, page]);

  const toggleSelect = (dish: Product) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(dish.id)) {
        next.delete(dish.id);
      } else {
        next.set(dish.id, dish);
      }
      return next;
    });
  };

  // Dishes already on this section would just be duplicates of themselves.
  const alreadyPlaced = useMemo(
    () =>
      new Set(
        (section?.menuItems ?? [])
          .map((item) => item.product?.id)
          .filter((id): id is string => !!id)
      ),
    [section]
  );

  const dishes = useMemo(
    () => loadedDishes.filter((dish) => !alreadyPlaced.has(dish.id)),
    [loadedDishes, alreadyPlaced]
  );

  const placeOne = (dish: Product, sortOrder: number) =>
    placeDish({
      data: {
        // No price given, so this menu inherits the dish's own price and
        // later edits to the dish reach this menu too.
        product: `/api/products/${dish.id}`,
        title: dish.title,
        description: dish.description ?? "",
        basePrice: dish.basePrice,
        sortOrder,
        isAvailable: true,
        menuSection: `/api/menu_sections/${section?.id}`,
        ...(dish.image?.id && { image: `/api/media_objects/${dish.image.id}` }),
      },
    }).unwrap();

  const handlePlaceSelected = async () => {
    if (!section?.id) {
      toast.error("Section invalide");
      return;
    }

    const toPlace = [...selected.values()];
    if (toPlace.length === 0) return;

    setIsPlacing(true);
    const base = section.menuItems?.length ?? 0;
    try {
      // Sequential so each placement's sortOrder follows the last one placed;
      // a failed dish doesn't stop the others (SFRESTO-150).
      const result = await placeDishesSequentially(toPlace, (dish, position) =>
        placeOne(dish, base + position + 1)
      );
      const summary = summarizeDishPlacement(result);

      if (result.placed.length > 0) onSuccess?.();

      if (summary.kind === "success") {
        toast.success(summary.message);
        onClose();
        return;
      }

      // Keep only the failed dishes ticked so the user can retry or untick them.
      const placedIds = new Set(result.placed.map((dish) => dish.id));
      setSelected(
        (prev) => new Map([...prev].filter(([id]) => !placedIds.has(id)))
      );
      toast.error(summary.message, { duration: 8000 });
    } finally {
      setIsPlacing(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Ajouter un plat existant</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Le même plat peut figurer sur plusieurs menus. Ses ventes, sa
            recette et ses marges restent regroupées.
          </p>
        </DialogHeader>

        <div className="space-y-4">
        <SearchInput
          placeholder="Rechercher un plat..."
          value={searchQuery}
          onChange={setSearchQuery}
        />

        {isLoading || (page === 1 && isFetching && dishes.length === 0) ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Chargement des plats...
          </p>
        ) : dishes.length === 0 && !hasMore ? (
          <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
            <UtensilsCrossed size={28} className="mb-2 opacity-50" />
            <p className="text-sm">
              {query.q
                ? "Aucun plat ne correspond à cette recherche"
                : "Tous vos plats sont déjà sur cette section"}
            </p>
          </div>
        ) : (
          <ul className="max-h-80 divide-y divide-border overflow-y-auto">
            {dishes.map((dish) => {
              const checked = selected.has(dish.id);
              return (
                <li key={dish.id}>
                  <div
                    role="button"
                    tabIndex={0}
                    aria-pressed={checked}
                    className="flex cursor-pointer items-center gap-3 py-2.5"
                    onClick={() => !isPlacing && toggleSelect(dish)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        if (!isPlacing) toggleSelect(dish);
                      }
                    }}
                  >
                    <Checkbox checked={checked} className="pointer-events-none" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">
                        {dish.title}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {displayPrice(dish.basePrice, currency)}
                      </p>
                    </div>
                  </div>
                </li>
              );
            })}
            {hasMore && (
              <li className="py-2 text-center">
                <Button
                  variant="ghost"
                  size="sm"
                  isLoading={isFetching}
                  disabled={isFetching}
                  onClick={() =>
                    setQuery((prev) => ({ ...prev, page: prev.page + 1 }))
                  }
                >
                  Afficher plus de plats
                </Button>
              </li>
            )}
          </ul>
        )}

        {(dishes.length > 0 || selected.size > 0) && (
          <div className="flex items-center justify-between border-t border-border pt-3">
            <span className="text-sm text-muted-foreground">
              {selected.size > 0
                ? `${selected.size} sélectionné${
                    selected.size > 1 ? "s" : ""
                  }`
                : "Sélectionnez un ou plusieurs plats"}
            </span>
            <Button
              size="sm"
              isLoading={isPlacing}
              disabled={selected.size === 0 || isPlacing}
              onClick={handlePlaceSelected}
            >
              {`Ajouter${selected.size > 0 ? ` (${selected.size})` : ""}`}
            </Button>
          </div>
        )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddExistingDishModal;
